import './OutreachTestMessageModal.scss';
import React, {
  FunctionComponent,
  KeyboardEvent,
  memo,
  useEffect,
  useState,
} from 'react';
import {
  Button,
  Chip,
  InputField,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  notify,
} from 'plume-ui';
import { ModalStyles } from 'plume-ui/dist/components/Modal/Modal';
import { useFormikContext } from 'formik';
import {
  outreachConfigEmailAtom,
  outreachConfigPushAtom,
  outreachTestMessageModalAtom,
} from '../../../state';
import { useRecoilState, useRecoilValue } from 'recoil';
import {
  MessageTypes,
  OutreachConfigResponse,
  OutreachSendTestSupportMessagePayload,
  OutreachSubscriberConfigResponse,
} from '../../Monitor/types';
import { DependencyContainer } from '../../../DependencyContainer';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FormattedMessage from '../../../utils/components/FormattedMessage';
import { OutreachTypes } from '../../../urls';
import { Maybe } from '../../../types';
import {
  preparePostPayloadForSubscriber,
  preparePostPayloadForSupport,
} from '../../Monitor/components/OutreachConfigForm/outreach-config-marshaller';
import { OutreachSubscriberPushDtoType } from '../../Monitor/components/OutreachConfigForm/dtos/outreach-support-email.dto';
import validateEmail from '../../../utils/isEmail/validateEmail';
import { OutreachConfigFormDto } from '../hooks/useOutreach';
import uuid from 'react-uuid';
import { useTrackEvent } from '../../trackingAnalytics/hooks/useTrackEvent';
import { MixPanelEvents } from '../../../mixPanelEvents';
import _ from 'lodash';
import EmailLanguageSelect from '../../Configuration/components/Emails/EmailLanguageSelect';

export type OutreachTestMessageModalProps = {
  isOpen: boolean;
  onRequestClose: () => void;
};

const { outreachService } = new DependencyContainer();

type ToastMessage = {
  success: string;
  failure: string;
};

const mapToastMessagesToOutreachType: Record<OutreachTypes, ToastMessage> = {
  [OutreachTypes.Email]: {
    success: 'outreach.form.testMessage.success',
    failure: 'outreach.form.testMessage.failure',
  },
  [OutreachTypes.Push]: {
    success: 'outreach.form.testNotification.success',
    failure: 'outreach.form.testNotification.failure',
  },
  [OutreachTypes.Webhooks]: {
    success: 'outreach.form.testWebhook.success',
    failure: 'outreach.form.testWebhook.failure',
  },
};

const OutreachTestMessageModal: FunctionComponent<OutreachTestMessageModalProps> = ({
  onRequestClose,
  isOpen,
}) => {
  const trackEvent = useTrackEvent();
  const { values } = useFormikContext();
  const [outreachTestMessageModal] = useRecoilState(
    outreachTestMessageModalAtom,
  );
  const outreachConfigEmail = useRecoilValue(outreachConfigEmailAtom);
  const outreachConfigPush = useRecoilValue(outreachConfigPushAtom);
  const [testRecipients, setTestRecipients] = useState<string[]>([]);
  const [currentRecipient, setCurrentRecipient] = useState<string>();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [fieldError, setFieldError] = useState<string>();
  const { partnerId, monitorId } = useParams<{
    partnerId: string;
    monitorId: string;
  }>();
  const { t } = useTranslation();

  useEffect(() => {
    if (!outreachTestMessageModal.isOpen) return;
    trackEvent({
      eventName: MixPanelEvents.OUTREACH_TEST_NOTIFICATION_MODAL_OPEN,
      additionalContent: {
        OUTREACH_TYPE: (outreachTestMessageModal.outreachType?.toUpperCase() ||
          'undefined type') as OutreachTypes | 'undefined type',
      },
    });
  }, [outreachTestMessageModal.isOpen]);

  const getPayloadFor = async (
    messageType: MessageTypes,
    outreachType: OutreachTypes,
  ) => {
    const formPayload =
      outreachType === OutreachTypes.Email
        ? preparePostPayloadForSupport(values as OutreachConfigFormDto)
        : preparePostPayloadForSubscriber(values as OutreachConfigFormDto);
    const keyToOmit =
      messageType === 'issue' ? 'clearMessageConfig' : 'issueMessageConfig';
    const baseTestMessagePayload = _.omit(formPayload, keyToOmit);
    const recipientsForMessage =
      outreachType === OutreachTypes.Email
        ? await outreachService.saveEmails(partnerId, testRecipients)
        : [];

    const withCTAUrlId = async (
      payload: OutreachSubscriberPushDtoType,
      messageType: MessageTypes,
    ) => {
      if (messageType === 'clear') {
        return payload;
      }
      const url = payload.issueMessageConfig.url;
      if (messageType === 'issue' && !url.enabled) {
        return payload;
      }
      const cta = outreachTestMessageModal.ctaUrl;
      return {
        ...payload,
        issueMessageConfig: {
          ...payload.issueMessageConfig,
          url: {
            ...payload.issueMessageConfig.url,
            urlLink: cta?.id,
          },
        },
      };
    };

    const getConfigObject = (
      outreachType: OutreachTypes,
    ): Maybe<OutreachConfigResponse[] | OutreachSubscriberConfigResponse[]> => {
      const mapOutreachTypeToConfig: Record<OutreachTypes, any> = {
        [OutreachTypes.Email]: outreachConfigEmail,
        [OutreachTypes.Webhooks]: outreachConfigEmail,
        [OutreachTypes.Push]: outreachConfigPush,
      };
      return mapOutreachTypeToConfig[outreachType];
    };

    const getRecipientsIds = (): string[] => {
      if (outreachType === OutreachTypes.Push) {
        return testRecipients;
      }
      return recipientsForMessage.map((e) => e.id);
    };

    const forIssue = async (outreachType: OutreachTypes) => {
      let payload: OutreachSendTestSupportMessagePayload<'issue'> = baseTestMessagePayload as OutreachSendTestSupportMessagePayload<
        'issue'
      >;
      const config = getConfigObject(outreachType);
      payload = {
        ...payload,
        issueMessageConfig: {
          ...payload.issueMessageConfig,
          recipients: getRecipientsIds(),
        },
      } as OutreachSendTestSupportMessagePayload<'issue'>;
      if (config && config.length && config[0].outreachId) {
        payload = {
          outreachId: config[0].outreachId,
          ...payload,
        } as OutreachSendTestSupportMessagePayload<'issue'>;
      }
      const withCTA =
        outreachType === OutreachTypes.Push
          ? await withCTAUrlId(payload as any, 'issue')
          : payload;
      return {
        recipients: testRecipients,
        language: selectedLanguage,
        ...withCTA,
      };
    };

    const forClear = (outreachType: OutreachTypes) => {
      let payload: OutreachSendTestSupportMessagePayload<'clear'> = baseTestMessagePayload as OutreachSendTestSupportMessagePayload<
        'clear'
      >;
      const config = getConfigObject(outreachType);

      payload = {
        ...payload,
        clearMessageConfig: {
          ...payload.clearMessageConfig,
          recipients: getRecipientsIds(),
        },
      } as OutreachSendTestSupportMessagePayload<'clear'>;

      if (config && config.length && config[0].outreachId) {
        payload = {
          outreachId: config[0].outreachId,
          ...payload,
        } as OutreachSendTestSupportMessagePayload<'clear'>;
      }

      return {
        recipients: testRecipients,
        language: selectedLanguage,
        ...payload,
      };
    };

    return () => {
      return messageType === 'clear'
        ? forClear(outreachType)
        : forIssue(outreachType);
    };
  };

  const send = async () => {
    const outreachType = outreachTestMessageModal?.outreachType;
    if (!outreachType) {
      return;
    }
    const payloadGetter = await getPayloadFor(
      outreachTestMessageModal.messageType,
      outreachType,
    );
    const payload = await payloadGetter();
    const toastMessages = mapToastMessagesToOutreachType[outreachType];

    try {
      await outreachService.sendTestMessage(
        partnerId,
        monitorId,
        payload as any,
        outreachTestMessageModal.messageType,
        outreachTestMessageModal.outreachType,
      );
      notify({
        title: t('success'),
        body: t(toastMessages.success),
        type: 'success',
      });
      onRequestClose();
      setTestRecipients([]);
      setCurrentRecipient(undefined);
    } catch (error) {
      notify({
        title: t('error'),
        body: t(toastMessages.failure),
        type: 'error',
      });
    }
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && currentRecipient) {
      if (
        !validateEmail(currentRecipient) &&
        outreachTestMessageModal.outreachType === OutreachTypes.Email
      ) {
        setFieldError(t('outreach.form.testMessage.incorrectEmail'));
        return;
      } else {
        setFieldError(undefined);
      }
      setTestRecipients((p) => [...p, currentRecipient]);
      setCurrentRecipient(undefined);
    }
  };

  const deleteTestRecipient = (index: number) => {
    setTestRecipients((recipients) => {
      const newRecipients = [...recipients];
      delete newRecipients[index];
      return newRecipients.filter((a) => a != null);
    });
  };

  const getMessagesForInputField = (): any => {
    if (fieldError) {
      return [
        {
          message: fieldError,
          status: 'error',
        },
      ];
    }
    return [];
  };

  const onClose = () => {
    onRequestClose();
    setFieldError(undefined);
    setTestRecipients([]);
    setCurrentRecipient(undefined);
    setSelectedLanguage('');
  };

  const inputLabelId =
    outreachTestMessageModal.outreachType === OutreachTypes.Push
      ? 'outreach.form.testNotification.addCustomerId'
      : '';
  const inputPlaceholderId =
    outreachTestMessageModal.outreachType === OutreachTypes.Push
      ? 'outreach.form.testNotification.placeholder'
      : 'outreach.form.testMessage.placeholder';

  const mapOutreachTypeToModalTitleId = {
    [OutreachTypes.Email]: 'outreach.form.testMessage.modalHeader',
    [OutreachTypes.Webhooks]: 'outreach.form.testWebhook.modalHeader',
    [OutreachTypes.Push]: 'outreach.form.testNotification.modalHeader',
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      classes={(current: ModalStyles) => ({
        ...current,
        root: `${current.root} OutreachTestMessageModal`,
      })}
    >
      <ModalHeader
        classes={(current) => ({
          ...current,
          root: `${current.root} OutreachTestMessageModal__header`,
        })}
        title={t(
          mapOutreachTypeToModalTitleId[
            outreachTestMessageModal?.outreachType ||
              OutreachTypes.Email ||
              OutreachTypes.Webhooks
          ],
        )}
      />
      <ModalBody
        classes={(current) => ({
          ...current,
          body: `${current.root} OutreachTestMessageModal__body`,
        })}
      >
        <InputField
          label={t(inputLabelId)}
          name="testRecipient"
          value={currentRecipient}
          messages={getMessagesForInputField()}
          onChange={(e) => setCurrentRecipient(e.currentTarget.value)}
          onKeyUp={onKeyUp}
          placeholder={t(inputPlaceholderId)}
        />
        {testRecipients && (
          <div className="OutreachTestMessageModal__recipients">
            {testRecipients.map((recipient, index) => (
              <Chip
                key={uuid()}
                color="dark"
                removable
                type="default"
                onClick={() => deleteTestRecipient(index)}
              >
                {recipient}
              </Chip>
            ))}
          </div>
        )}
        <EmailLanguageSelect
          onChange={(value) => setSelectedLanguage(value)}
          value={selectedLanguage}
        />
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          styleVariant="tertiary-grey"
          onClick={() => onClose()}
        >
          <FormattedMessage id="cancel" />
        </Button>
        <Button
          styleVariant="superprimary"
          type="button"
          disabled={testRecipients.length === 0}
          onClick={() => send()}
          classes={(current) => ({
            ...current,
            root: `${current.root} m-l-xs`,
          })}
        >
          <FormattedMessage id="outreach.form.testMessage.sendTest" />
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default memo(OutreachTestMessageModal);
